import { fragmentTypes } from '@paintscout/api'
import { isMasqueradeCompanyNotFoundError } from '@paintscout/util/users'
import * as Sentry from '@sentry/react'
import { filterKeys } from '@wora/cache-persist/lib/layers/filterKeys'
import { IntrospectionFragmentMatcher } from 'apollo-cache-inmemory'
import type { MutationOptions, OperationVariables } from 'apollo-client'
import { ApolloLink } from 'apollo-link'
import { setContext } from 'apollo-link-context'
import { onError } from 'apollo-link-error'
import { createHttpLink } from 'apollo-link-http'
import { RetryLink } from 'apollo-link-retry'
import { getMainDefinition } from 'apollo-utilities'
import type { OperationDefinitionNode } from 'graphql'
import { info } from '../util/analytics'
import { auth } from '../util/auth'
import { createAuthFailureHandler } from '../util/auth/authRecovery'
import type { AuthTokenResult } from '../util/auth/createAuthTokenGetter'
import { createAuthTokenGetter, isInvalidGrant } from '../util/auth/createAuthTokenGetter'
import ApolloClientIDB from './ApolloClientIDB'
import { ignoredOfflineErrors } from './util/ignoredOfflineErrors'
import { getMasqueradeHeaders } from './masquerade'

const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: Infinity,
    jitter: true
  },
  attempts: {
    max: 3,
    retryIf: (error, operation) => {
      // exclude these operations from the retry
      const skipOperations = [
        'processEvent',
        'acceptQuote',
        'declineQuote',
        'acceptAdditionalWork',
        'sendQuote',
        'requestPayment',
        // Sends emails. A retry after a lost response would send every assignee the grouped
        // checklist email again (#24519).
        'notifyChecklistAssignees',
        // quotePdf can legitimately take ~20s for large/photo-heavy quotes; on a slow/failed
        // render the server returns an ECONNABORTED sentinel and emails a link async. Retrying
        // 3x here only piles up 20s+ requests and drags out the spinner for minutes (#19008).
        'quotePdf'
      ]
      const is413Error = error && error.networkError && error.networkError.statusCode === 413
      // the masqueraded company can't be loaded — every retry fails identically at context creation,
      // so retrying just delays the recovery screen by 3 round trips
      const isMasqueradeCompanyMissing = isMasqueradeCompanyNotFoundError(error)
      return !!error && !skipOperations.includes(operation.operationName) && !is413Error && !isMasqueradeCompanyMissing
    }
  }
})

const errorLink = onError(({ graphQLErrors, networkError, operation: _operation, response: _response }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations: _locations, path }) =>
      console.warn(`[GraphQL error] "${path}": ${message}`)
    )
  }
  if (networkError) {
    console.warn(`[Network error]: ${networkError}`)
  }
})

/**
 * Remove __typename from mutation input
 *
 * workaround for https://github.com/apollographql/apollo-feature-requests/issues/6
 *
 * we should remove this in favour of the removeTypenames function, and use that to sanitize input for mutations
 *
 */
const cleanTypenameLink = new ApolloLink((operation, forward) => {
  const omitTypename = (key: any, value: any) => (key === '__typename' ? undefined : value)

  const def = getMainDefinition(operation.query)
  if (def && (def as OperationDefinitionNode).operation === 'mutation' && !operation.operationName?.match(/upload/)) {
    operation.variables = JSON.parse(JSON.stringify(operation.variables), omitTypename)
  }
  return forward ? forward(operation) : null
})

// fix graphql requests not showing in logrocket - recommended solution
// https://docs.logrocket.com/docs/troubleshooting-sessions#section-apollo-client
const fetcher = (...args: any[]) => {
  // @ts-ignore
  return window.fetch(...args)
}

const httpLink = createHttpLink({
  uri: `${process.env.PS_GRAPHQL_URL}`,
  fetch: fetcher
})

/**
 * Token acquisition for the main app's Auth0 client.
 *
 * The implementation is shared with painter-view and admin — see
 * `shared/util/auth/createAuthTokenGetter.ts` for the bound, the single-flight, and the long note
 * on why `cacheMode` is always `'on'` (ENG-1190). Re-exported here so the many existing importers
 * of `shared/graphql/client` keep working.
 */
// `onAuthFailure`: a refresh token that dies mid-session (`invalid_grant` / missing) now sends
// the user through a real login immediately, instead of every operation retrying the dead token
// and failing `jwt must be provided` until they log out by hand (ENG-1190).
const { getAuthToken, getAuthTokenResult } = createAuthTokenGetter(auth, 'getAuthToken', {
  onAuthFailure: createAuthFailureHandler(auth)
})

export { getAuthToken, getAuthTokenResult, isInvalidGrant }
export type { AuthTokenResult }

const getDevMockHeader = (): Record<string, string> => {
  if (process.env.DEPLOYMENT_STAGE === 'master') return {}
  try {
    const raw = window.localStorage.getItem('ps:devtools:permissions')
    const mock = raw ? JSON.parse(raw) : null
    if (mock?.active) return { 'x-dev-mock': JSON.stringify(mock) }
  } catch {}
  return {}
}

/**
 * Auth link bound to a single masqueraded company for the lifetime of the client.
 *
 * The company id is captured here rather than read from storage on every request. Reading it per
 * request meant the header could change under a client that was still rendering the previous
 * company — during a switch, or (before masquerade became per-tab) whenever another tab switched —
 * and a mutation could be written into a different customer's database than the one on screen.
 * A client now speaks to exactly one company, and switching companies builds a new client.
 */
function createAuthLink(masqueradeCompanyId?: string | null) {
  return setContext(async (_, { headers }) => {
    const token = await getAuthToken()
    const devMockHeader = getDevMockHeader()

    // Built once so the no-token branch cannot drift from the authenticated one. It used to return
    // only x-app-version and the dev mock header, dropping both the incoming `headers` and the
    // masquerade headers — so a request made while the token was missing also lost the company it
    // was addressed to. The API rejects it either way with `jwt must be provided`, which is what
    // drives recovery; losing the masquerade target on top of that is a separate defect (ENG-1190).
    const baseHeaders = {
      ...headers,
      'x-app-version': `${process.env.VERSION}-${process.env.COMMIT}`,
      ...getMasqueradeHeaders(masqueradeCompanyId ?? null),
      ...devMockHeader
    }

    if (!token) {
      return { headers: baseHeaders }
    }

    return { headers: { ...baseHeaders, Authorization: `Bearer ${token}` } }
  })
}

export function createApolloClient(opts: {
  idbName: string
  enableOffline: boolean
  masqueradeCompanyId?: string | null
}) {
  const { idbName, enableOffline, masqueradeCompanyId } = opts
  const authLink = createAuthLink(masqueradeCompanyId)

  const apolloClient = ApolloClientIDB.create(
    {
      link: ApolloLink.from([authLink, errorLink, cleanTypenameLink, retryLink, httpLink]),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'cache-and-network',
          notifyOnNetworkStatusChange: true,
          returnPartialData: true
        }
      }
    },
    {
      idbName,
      cacheOptions: {
        fragmentMatcher: new IntrospectionFragmentMatcher({
          introspectionQueryResultData: fragmentTypes
        }),
        dataIdFromObject: (o: any) => {
          function getId() {
            switch (o.__typename) {
              case 'ActivityQuote':
              case 'ActivityContact':
              case 'ActivityDeal':
              case 'QuoteContact':
                // prevent caching these types because we don't want them normalized (their ids may match but their data is different)
                // apollo client v3 will let us deal with this in a better way
                return null
              case 'User':
                return o.user_id
              default:
                return o._id || o.id
            }
          }

          let id = getId()

          // document ids starting with $ aren't allowed in apollo
          // so we'll replace it with _$ - hopefully this doesn't come back to bite us
          if (typeof id === 'string') {
            id = id.replace(/^\$/, '_$')
          }

          if (id) {
            // return <typename>:<id>
            if (o.__typename) {
              return `${o.__typename + ':'}${id}`
            }

            // return just id
            return id
          }

          // no id, return null
          return null
        }
      },
      persistOptions: {
        disablePersist: !enableOffline,
        mutateValues: [
          {
            set(value) {
              return value
            },
            get(value) {
              return value
            }
          }
        ],

        mutateKeys: [
          filterKeys((key) => {
            // ignore query keys that match this regex
            const ignore = /search|exportQuotes|quoteUrl/i

            // we want to save searchUsers, as it will be a small list anyway
            if (key.includes('searchUsers')) {
              return true
            }

            return !ignore.test(key)
          })
        ]
      }
    }
  )

  const ignoredErrors = ignoredOfflineErrors
  apolloClient.setOfflineOptions({
    // called once the request has been successfully completed.
    // Only if the function returns the value true, the request is deleted from the queue.
    async onComplete(options) {
      if (!enableOffline) {
        return true
      }
      if (options?.response?.errors) {
        if (
          options.response.errors.some(({ message }: { message: string }) =>
            ignoredErrors.some((ignored) => message.match(ignored))
          )
        ) {
          return true
        }

        Sentry.captureException(new Error('Unable to send persisted mutation'))
        return false
      }
      return true
    },
    // called when the request returns an error.
    // Only if the function returns the value true, the mutation is deleted from the queue
    async onDiscard(options: any) {
      if (!enableOffline) {
        return true
      }

      if (options.error) {
        if (ignoredErrors.some((err) => options.error.message.match(err))) {
          // return false
          return true
        }

        const error = new Error('Unable to send persisted mutation')

        // trimmed down version to get the variables for said mutation
        console.info('failed mutation:', {
          ...options,
          offlinePayload: {
            ...options.offlinePayload,
            request: {
              payload: {
                variables: options.offlinePayload?.request?.payload?.variables
              }
            }
          }
        })

        info('failed mutation:', {
          ...options,
          offlinePayload: {
            ...options.offlinePayload,
            request: {
              payload: {
                variables: options.offlinePayload?.request?.payload?.variables
              }
            }
          }
        })

        throw error
      }

      return true
    },
    // called before saving the mutation in the store
    async onPublish(payload) {
      // if a mutation contains "onlineOnly" in the context, we will error the mutation (assuming we are offline)
      if (!enableOffline || payload?.request?.payload?.context?.onlineOnly) {
        console.log(payload?.request?.payload?.context ?? payload?.request?.payload ?? payload?.request ?? payload)
        console.log({ enableOffline, onlineOnly: payload?.request?.payload?.context?.onlineOnly })
        throw Error('Request can not be made offline')
      } else {
        return payload
      }
    },
    manualExecution: true // <ApolloQueue /> component will manage sending of offline requests
  })

  // hacky solution to storing mutations as offline mutations if response is a network error
  const originalMutate = apolloClient.mutate

  apolloClient.mutate = async function <T = any, TVariables = OperationVariables>(
    options: MutationOptions<T, TVariables>
  ) {
    try {
      const response = await originalMutate<T, TVariables>(options)

      return response
    } catch (error) {
      if (error.message?.match(/Network error/)) {
        return apolloClient.mutateOffline<T>(options)
      }
      throw error
    }
  }

  if (process.env.NODE_ENV === 'development') {
    // @ts-ignore
    window.apolloClient = apolloClient
  }

  return apolloClient
}

// Vite HMR: invalidate module on hot update so Apollo client is re-created.
// Uses indirect eval to hide `import.meta.hot` from webpack's parser (webpack 4
// doesn't support import.meta.hot and will fail at parse time).
try {
  const hot = new Function('return import.meta.hot')()
  if (hot) hot.invalidate()
} catch (_e) {
  // Not in a Vite HMR environment — ignore
}
